//macadamia-app/src/macadamia-core/components/postits/PostItColorPicker.tsx
import React, { useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { POSTIT_COLORS } from './types';

type PostItColor = 'yellow' | 'pink' | 'blue' | 'green';

interface PostItColorPickerProps {
  currentColor: PostItColor;
  onColorChange: (color: PostItColor) => void;
  onClose: () => void;
  position: { top: number; left: number };
}

const PostItColorPicker: React.FC<PostItColorPickerProps> = ({
  currentColor,
  onColorChange,
  onClose,
  position
}) => {
  const pickerRef = useRef<HTMLDivElement>(null);

  // Fermer au clic en dehors
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    // Ajouter le listener après un petit délai pour éviter la fermeture immédiate
    setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 0);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose]);

  const colors: PostItColor[] = ['yellow', 'pink', 'blue', 'green'];

  const handleColorClick = (color: PostItColor) => {
    onColorChange(color);
    onClose();
  };

  return (
    <AnimatePresence>
      <motion.div
        ref={pickerRef}
        initial={{ opacity: 0, y: -10, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: -10, scale: 0.95 }}
        transition={{ duration: 0.2 }}
        className="absolute bg-white dark:bg-gray-800 shadow-lg rounded-lg p-2 z-50"
        style={{
          top: `${position.top}px`,
          left: `${position.left}px`,
        }}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="flex flex-col gap-2">
          {colors.map((color) => {
            const colorTheme = POSTIT_COLORS[color];
            const isActive = color === currentColor;

            return (
              <button
                key={color}
                onClick={() => handleColorClick(color)}
                onMouseDown={(e) => e.stopPropagation()}
                className="relative w-7 h-7 rounded-full transition-transform hover:scale-110 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                style={{
                  background: colorTheme.gradient,
                  border: isActive ? '3px solid white' : '1px solid rgba(0,0,0,0.1)',
                  boxShadow: isActive
                    ? `0 0 0 2px ${colorTheme.shadow}, 0 4px 6px ${colorTheme.shadow}`
                    : `0 2px 4px ${colorTheme.shadow}`,
                }}
                title={color.charAt(0).toUpperCase() + color.slice(1)}
                aria-label={`Select ${color} color`}
              >
                {isActive && (
                  <span
                    className="absolute inset-0 flex items-center justify-center text-white font-bold text-sm"
                    style={{ textShadow: '0 1px 2px rgba(0,0,0,0.3)' }}
                  >
                    
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default PostItColorPicker;
